import React, { useCallback } from 'react'

import { useDrop, useDrag } from 'react-dnd'
import update from 'immutability-helper'
import PropTypes from 'prop-types'

const DraggableImageElement = ({ id, left, top, hideSourceOnDrag, currentWidth, currentOrientation, setImage }) => {
  const [{ isDragging }, drag, connectDragPreview] = useDrag(
    () => ({
      type: 'box',
      item: { id, left, top },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    }),
    [id, left, top]
  )

  if (isDragging && hideSourceOnDrag) {
    return <div ref={drag} />
  }

  const getMarginValue = () => {
    if (window.innerWidth >= 1400) {
      return '22.5%'
    } else if (window.innerWidth >= 1200) {
      return '15%'
    } else if (window.innerWidth >= 992) {
      return '10%'
    } else if (window.innerWidth >= 768) {
      return '5%'
    }
    return '0%'
  }

  return (
    <div
      className="box position-absolute"
      id='ImageContainer'
      ref={drag}
      style={{
        cursor: 'move',
        marginLeft: getMarginValue(),
        left,
        top,
        width: `${currentWidth}%`,
        transform: `translate3d(0, 0, 0) rotate(${currentOrientation}deg)`,
        boxShadow: 'none'
      }}
      data-testid="box"
    >
      <img
        className='w-100'
        style={{
          imageRendering: 'auto',
          transform: 'translate3d(0, 0, 0)',
          boxShadow: 'none'
        }}
        src={setImage}
        alt="ImagePreview"
      />
    </div>
  )
}

export const DocumentPreviewContainer = ({ hideSourceOnDrag, currentWidth, setImage, currentOrientation, draggableImageElement, setDraggableImageElements }) => {
  const moveDraggableImageElement = useCallback(
    (id, left, top) => {
      setDraggableImageElements(update(draggableImageElement, {
        [id]: {
          $merge: { left, top }
        }
      }))
    },
    [draggableImageElement, setDraggableImageElements]
  )

  const [, drop] = useDrop(
    () => ({
      accept: 'box',
      drop(item, monitor) {
        const delta = monitor.getDifferenceFromInitialOffset()
        const left = Math.round(item.left + delta.x)
        const top = Math.round(item.top + delta.y)
        moveDraggableImageElement(item.id, left, top)
        return undefined
      }
    }),
    [moveDraggableImageElement]
  )

  return (
    <div className='w-100 position-relative p-4' ref={drop} style={{ height: '100vh', boxSizing: 'border-box' }}>
      {Object.keys(draggableImageElement).map((key) => {
        const { left, top, image } = draggableImageElement[key]
        return (
          <DraggableImageElement
            key={key}
            id={key}
            left={left}
            top={top}
            hideSourceOnDrag={hideSourceOnDrag}
            currentWidth={currentWidth}
            setImage={setImage}
            currentOrientation={currentOrientation}
          >{image}</DraggableImageElement>
        )
      })}
    </div>
  )
}

DocumentPreviewContainer.propTypes = {
  draggableImageElement: PropTypes.objectOf(PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number,
    image: PropTypes.string
  }))
}

DraggableImageElement.propTypes = {
  id: PropTypes.string,
  left: PropTypes.number,
  top: PropTypes.number,
  hideSourceOnDrag: PropTypes.bool,
  currentWidth: PropTypes.number,
  currentOrientation: PropTypes.number,
  setImage: PropTypes.string
}

export default DocumentPreviewContainer
